import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  banderaDe,
  esPaisConocido,
  nombrePais,
  paisesOrdenados,
  partirTelefono,
  prefijoDe,
} from '@alexiarest/shared';

interface PhoneFieldProps {
  label: ReactNode;
  hint?: ReactNode;
  error?: string;
  required?: boolean;
  /** Numero completo en E.164 («+34600123456») o vacio. */
  value: string;
  onChange: (value: string) => void;
  /** Pais del restaurante: propone su prefijo mientras el usuario no elija otro. */
  defaultCountry?: string;
  disabled?: boolean;
}

interface OpcionPrefijo {
  code: string;
  /** Lo que se ve en la lista: «🇲🇽 +52 · México». */
  label: string;
  /** Lo que se ve en la casilla con la lista cerrada: «🇲🇽 +52». */
  corto: string;
  /** Nombre, prefijo y codigo ISO sin acentos, para buscar. */
  busqueda: string;
}

/** Sin acentos y en minusculas: «méxico» y «mexico» tienen que encontrar lo mismo. */
const plano = (v: string): string =>
  v
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const OPCIONES_PREFIJO: OpcionPrefijo[] = paisesOrdenados().map((p) => {
  const nombre = nombrePais(p.code) ?? p.code;
  return {
    code: p.code,
    label: `${banderaDe(p.code)} ${p.dial} · ${nombre}`,
    corto: `${banderaDe(p.code)} ${p.dial}`,
    busqueda: plano(`${nombre} ${p.dial} ${p.code}`),
  };
});

/** Tope de opciones pintadas: la lista entera son ~250 países. */
const MAX_VISIBLES = 40;

const soloCifras = (v: string): string => v.replace(/\D/g, '');

/**
 * Telefono en dos cajas: prefijo del pais y numero. Hacia fuera es un solo
 * valor en E.164, que es lo que guarda la API y lo que abre WhatsApp.
 *
 * Por que dos cajas y no una: con una sola, la mitad de los clientes de
 * America dejaba el numero sin prefijo y no habia forma de escribirles
 * (7 sep 2026). El prefijo sale ya puesto segun el pais del restaurante.
 *
 * Por que el prefijo se BUSCA y no es un desplegable (mejora 71, Alex, 8 sep
 * 2026): son ~250 paises y un `<select>` nativo solo salta por las primeras
 * letras de la etiqueta, que aqui es la bandera. Escribiendo «mex» —o «+52»,
 * o «MX»— aparece Mexico.
 */
export function PhoneField({
  label,
  hint,
  error,
  required,
  value,
  onChange,
  defaultCountry,
  disabled,
}: PhoneFieldProps): JSX.Element {
  const id = useId();
  const hintId = `${id}-hint`;
  const paisPorDefecto = esPaisConocido(defaultCountry) ? defaultCountry!.toUpperCase() : 'ES';

  const [inicial] = useState(() => partirTelefono(value, paisPorDefecto));
  const [pais, setPais] = useState(inicial.country ?? paisPorDefecto);
  const [nacional, setNacional] = useState(inicial.national);
  // El prefijo sigue al pais del restaurante hasta que el usuario lo toca o
  // hasta que el numero ya venia con uno.
  const [prefijoFijado, setPrefijoFijado] = useState(inicial.dial !== '');

  // Estado del buscador de prefijo.
  const [texto, setTexto] = useState('');
  const [abierto, setAbierto] = useState(false);
  const [resaltado, setResaltado] = useState(0);
  const caja = useRef<HTMLDivElement>(null);

  const emitir = (p: string, n: string) => {
    const cifras = soloCifras(n);
    onChange(cifras ? `${prefijoDe(p)}${cifras}` : '');
  };

  // Un numero guardado sin prefijo (dato antiguo) se completa con el del pais
  // nada mas pintarse: si no, el siguiente «Guardar» lo rechazaria.
  useEffect(() => {
    if (value && inicial.dial === '' && inicial.national) emitir(pais, inicial.national);
    // Solo al montar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (prefijoFijado) return;
    setPais((actual) => {
      if (actual === paisPorDefecto) return actual;
      if (soloCifras(nacional)) emitir(paisPorDefecto, nacional);
      return paisPorDefecto;
    });
    // Reacciona solo al cambio de pais del restaurante.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paisPorDefecto, prefijoFijado]);

  useEffect(() => {
    if (!abierto) return;
    const fuera = (e: MouseEvent) => {
      if (caja.current && !caja.current.contains(e.target as Node)) setAbierto(false);
    };
    document.addEventListener('mousedown', fuera);
    return () => document.removeEventListener('mousedown', fuera);
  }, [abierto]);

  const elegida = OPCIONES_PREFIJO.find((o) => o.code === pais);
  const filtradas = useMemo(() => {
    const aguja = plano(texto.trim());
    if (aguja === '') return OPCIONES_PREFIJO;
    // El «+» se teclea o no: «+52» y «52» buscan lo mismo.
    const sinMas = aguja.replace(/^\+/, '');
    return OPCIONES_PREFIJO.filter(
      (o) => o.busqueda.includes(aguja) || o.busqueda.includes(sinMas),
    );
  }, [texto]);
  // Solo se navega y se elige sobre lo que SE VE: con 250 países, bajando
  // bastante se acabaría eligiendo con Enter uno que no está en pantalla.
  const mostradas = useMemo(() => filtradas.slice(0, MAX_VISIBLES), [filtradas]);

  const elegirPais = (code: string) => {
    setPais(code);
    setPrefijoFijado(true);
    setTexto('');
    setAbierto(false);
    emitir(code, nacional);
  };

  const onNumero = (raw: string) => {
    // Pegar «+39 333 123 4567» entero en la caja del numero tambien vale.
    if (raw.trim().startsWith('+')) {
      const partes = partirTelefono(raw, pais);
      if (partes.country) {
        setPais(partes.country);
        setPrefijoFijado(true);
        setNacional(partes.national);
        emitir(partes.country, partes.national);
        return;
      }
    }
    const limpio = raw.replace(/[^\d\s]/g, '');
    setNacional(limpio);
    emitir(pais, limpio);
  };

  return (
    <div className={`field ${error ? 'field--error' : ''}`.trim()}>
      <label className="field__label" htmlFor={id}>
        {label}
        {required && <span className="field__required" aria-hidden="true"> *</span>}
      </label>
      <div className="telefono">
        <div className="telefono__pais buscador" ref={caja}>
          <input
            className="input telefono__prefijo"
            type="text"
            autoComplete="off"
            role="combobox"
            aria-label="Prefijo del país"
            aria-expanded={abierto}
            aria-controls={`${id}-paises`}
            disabled={disabled}
            // Cerrado ensenya el prefijo elegido; abierto, lo que se escribe.
            value={abierto ? texto : (elegida?.corto ?? pais)}
            placeholder="País o prefijo"
            onFocus={() => {
              setAbierto(true);
              setTexto('');
              setResaltado(0);
            }}
            onChange={(event) => {
              setTexto(event.target.value);
              setAbierto(true);
              setResaltado(0);
            }}
            onKeyDown={(event) => {
              if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                event.preventDefault();
                setAbierto(true);
                setResaltado((i) => {
                  const paso = event.key === 'ArrowDown' ? 1 : -1;
                  const total = mostradas.length;
                  return total === 0 ? 0 : (i + paso + total) % total;
                });
              } else if (event.key === 'Enter' && abierto) {
                event.preventDefault();
                const opcion = mostradas[resaltado];
                if (opcion) elegirPais(opcion.code);
              } else if (event.key === 'Escape') {
                setAbierto(false);
              }
            }}
          />
          {abierto && (
            <ul className="buscador__lista telefono__lista" id={`${id}-paises`} role="listbox">
              {filtradas.length === 0 && <li className="buscador__vacio">Ningún país coincide</li>}
              {mostradas.map((option, i) => (
                <li key={option.code}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={option.code === pais}
                    className={`buscador__opcion ${i === resaltado ? 'is-resaltada' : ''}`.trim()}
                    onMouseEnter={() => setResaltado(i)}
                    onClick={() => elegirPais(option.code)}
                  >
                    {option.label}
                  </button>
                </li>
              ))}
              {filtradas.length > MAX_VISIBLES && (
                <li className="buscador__vacio">
                  Y {filtradas.length - MAX_VISIBLES} más: escribe el nombre del país.
                </li>
              )}
            </ul>
          )}
        </div>
        <input
          id={id}
          className="input"
          type="tel"
          inputMode="tel"
          autoComplete="tel-national"
          value={nacional}
          onChange={(event) => onNumero(event.target.value)}
          placeholder="600 000 000"
          required={required}
          disabled={disabled}
          aria-describedby={hint || error ? hintId : undefined}
          aria-invalid={error ? true : undefined}
        />
      </div>
      {(error || hint) && (
        <p className="field__hint" id={hintId}>
          {error ?? hint}
        </p>
      )}
    </div>
  );
}
